import React, { ChangeEvent, useEffect, useState } from 'react';
import SelectDropdown from '../../reusable/SelectDropdown';
import { InputLabel, TextField } from '@mui/material';
import CustomButton from '../../reusable/CustomButton';
import useUserDetails from '../../hooks/useUserDetails';
import useErrorModal from '../../hooks/useErrorModal';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { getSubjectAreas, saveAcademicSettings } from '../../../services/eoaConfigServices';
import CheckboxGroup from '../CheckBoxGroup';
import { useAuth0 } from '@auth0/auth0-react';

import './AcademicSettings.css';

const numericInputProps = { min: 0, pattern: '^[1-9]d*$' };

const isValidNumericInput = (value: string) => {
  return value === '' || /^[0-9]+$/.test(value);
};

const gradingSystemTypes = [{ name: 'Numeric' }, { name: 'Letter' }, { name: 'Other' }];

function AcademicSettings() {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { getAccessTokenSilently } = useAuth0();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const navigate = useNavigate();

  const [gradingSystem, setGradingSystem] = useState('');
  const [failingGrade, setFailingGrade] = useState<number | ''>('');
  const [academicTermsCount, setAcademicTermsCount] = useState<number | ''>('');
  const [creditsForCourse, setCreditsForCourse] = useState<number | ''>('');
  const [requiredSatForGraduation, setRequiredSatForGraduation] = useState(false);
  const [requiredActForGraduation, setRequiredActForGraduation] = useState(false);
  const [cteCoursesOffered, setCteCoursesOffered] = useState(false);
  const [requiredCreditsPerSubjectArea, setRequiredCreditsPerSubjectArea] = useState<
    { subject: string; required_credits: number }[]
  >([]);

  useEffect(() => {
    fetchSubjectAreas();
  }, []);

  const fetchSubjectAreas = async () => {
    try {
      let access_token = await getAccessTokenSilently();
      let response = await getSubjectAreas(access_token);

      const subjectAreasWithCredits = response.data.map(({ name }) => ({
        subject: name,
        required_credits: 0,
      }));

      setRequiredCreditsPerSubjectArea(subjectAreasWithCredits);
    } catch (error) {
      handleOpenErrorModal('Error occured while fetching subject areas!', error as AxiosError);
    }
  };

  const handleGradingSystem = (event: { target: { value: string } }) => {
    setGradingSystem(event.target.value);
    setFailingGrade('');
  };

  const handleChange =
    <T extends number | string>(setter: React.Dispatch<React.SetStateAction<T>>) =>
    (event: { target: { name: string; value: string } }) => {
      const { name, value } = event.target;

      if (name === 'failingGrade' && gradingSystem === 'Letter') {
        if (/\d/.test(value)) return;
        setter(value as T);
      }
      //prevent NaN value in the text field if user enters non-digit symbol
      else if (isValidNumericInput(value)) setter(Math.max(0, Number(value)) as T);
    };

  const handleChangeCreditsPerSubjectArea =
    (subject: string) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let { value } = event.target;

      //prevent NaN value in the text field if user enters non-digit symbol
      if (isValidNumericInput(value)) {
        setRequiredCreditsPerSubjectArea((prev) =>
          prev.map((course) =>
            course.subject === subject ? { ...course, required_credits: Math.max(0, Number(value)) } : course
          )
        );
      }
    };

  const handleSaveAcademicSettings = async () => {
    const academicSettingsData = {
      sat_required: requiredActForGraduation,
      act_required: requiredActForGraduation,
      grading_system: gradingSystem.toUpperCase(),
      failing_grade: failingGrade ? failingGrade : 0,
      academic_term_count: academicTermsCount ? academicTermsCount : 0,
      default_credits_per_course: creditsForCourse ? creditsForCourse : 0,
      cte_courses_offered: cteCoursesOffered,
      graduation_requirements: { required_courses: requiredCreditsPerSubjectArea },
    };

    if (userDetails) {
      try {
        await saveAcademicSettings(userDetails.eoaId, userDetails.access_token, academicSettingsData);
        navigate('/eoa');
      } catch (error) {
        console.error(error);
        handleOpenErrorModal('Error occured while saving academic settings!', error as AxiosError);
      }
    }
  };

  const checkBoxes = [
    { name: 'sat', value: requiredSatForGraduation, label: 'Required SAT for graduation' },
    { name: 'act', value: requiredActForGraduation, label: 'Required ACT for graduation' },
    { name: 'cte', value: cteCoursesOffered, label: 'CTE Courses Offered' },
  ];

  const handleCheckboxChange = (name: string, checked: boolean) => {
    const setters: Record<string, React.Dispatch<React.SetStateAction<boolean>>> = {
      sat: setRequiredSatForGraduation,
      act: setRequiredActForGraduation,
      cte: setCteCoursesOffered,
    };

    setters[name]?.(checked);
  };

  return (
    <>
      <div className="academic-settings-data-fields">
        <h3>EOA Academic Settings</h3>
        <InputLabel>Grading System</InputLabel>
        <SelectDropdown
          label=""
          selectedValue={gradingSystem}
          handleChange={handleGradingSystem}
          handleDelete={() => setGradingSystem('')}
          items={gradingSystemTypes.map((gradingSystemType) => ({
            label: gradingSystemType.name,
            value: gradingSystemType.name,
          }))}
          formControlVariant="outlined"
        />
        {gradingSystem && gradingSystem !== 'Other' && (
          <>
            <InputLabel>Failing Grade</InputLabel>
            <TextField
              required
              variant="outlined"
              name="failingGrade"
              value={failingGrade}
              InputProps={{ inputProps: numericInputProps }}
              onChange={handleChange(setFailingGrade)}
            />
          </>
        )}
        <InputLabel>Number of Academic Terms</InputLabel>
        <TextField
          required
          variant="outlined"
          value={academicTermsCount}
          InputProps={{ inputProps: numericInputProps }}
          onChange={handleChange(setAcademicTermsCount)}
        />
        <InputLabel>Standard Credits for Course</InputLabel>
        <TextField
          required
          variant="outlined"
          value={creditsForCourse}
          InputProps={{ inputProps: numericInputProps }}
          onChange={handleChange(setCreditsForCourse)}
        />
        {requiredCreditsPerSubjectArea.map(({ subject, required_credits }) => (
          <React.Fragment key={subject}>
            <InputLabel>{`# of credits required to graduate in ${subject}`}</InputLabel>
            <TextField
              required
              variant="outlined"
              value={required_credits ? required_credits : 0}
              InputProps={{ inputProps: numericInputProps }}
              onChange={handleChangeCreditsPerSubjectArea(subject)}
            />
          </React.Fragment>
        ))}
        <CheckboxGroup checkboxes={checkBoxes} onChange={handleCheckboxChange} />
        <CustomButton
          variant="primary"
          size="medium"
          buttonText="Save Academic Settings"
          buttonAction={handleSaveAcademicSettings}
        />
      </div>
    </>
  );
}

export default AcademicSettings;
